import AuditQuestionCard from "../../../components/ui/containers/AuditQuestionCard";
import styles from "./FormPrompt.module.css";
import { useEffect, useState } from "react";
import TextArea from "../../../components/ui/input/TextArea";
import CameraInput from "../../../components/ui/buttons/CameraInput";
import ActivityIndicator from "../../../components/ui/activityIndicator/ActivityIndicator";
import InfoPrompt from "../../../components/ui/prompts/InfoPrompt";
import ImageListCard from "../../../components/ui/containers/ImageListCard";
import ImagePreviewPrompt from "../../../components/ui/prompts/ImagePreviewPrompt";
import {
  createImgObject,
  processUploadedImage,
  resizeFile,
} from "../../../hooks/useProcessImage";

const Question = ({ questionGUID, question, inputHandler }) => {
  const maxPhotoCount = 4;
  const [yesFocused, setYesFocused] = useState(false);
  const [noFocused, setNoFocused] = useState(false);
  const [naFocused, setNAFocused] = useState(false);
  const [notes, setNotes] = useState("");
  const [inputGiven, setInputGiven] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    updatePhotoInput(images);
    // eslint-disable-next-line
  }, [images]);

  const yesInputHandler = () => {
    setYesFocused(true);
    setNoFocused(false);
    setNAFocused(false);
    resetQuestion();
  };

  const noInputHandler = () => {
    setYesFocused(false);
    setNoFocused(true);
    setNAFocused(false);
    resetQuestion();
  };

  const naInputHandler = () => {
    setYesFocused(false);
    setNoFocused(false);
    setNAFocused(true);
    resetQuestion();
  };

  const resetQuestion = () => {
    setNotes("");
    setInputGiven(false);
    inputHandler(questionGUID, {
      questionGUID: "",
      photos: null,
      textInput: "",
      valid: false,
      value: "",
    });
    setImages([]);
  };

  const notesChangeHandler = (event) => {
    setNotes(event.target.value);
    let inputButtonText = yesFocused ? "YES" : noFocused ? "NO" : "N/A";
    if (inputButtonText === "NO") {
      setInputGiven(event.target.value.length > 0 && images.length > 0);
      inputHandler(questionGUID, {
        questionGUID: questionGUID,
        value: inputButtonText,
        textInput: event.target.value,
        photos: images,
        valid: event.target.value.length > 0 && images.length > 0,
      });
    } else {
      setInputGiven(event.target.value.length > 0);
      inputHandler(questionGUID, {
        questionGUID: questionGUID,
        value: inputButtonText,
        textInput: event.target.value,
        photos: null,
        valid: event.target.value.length > 0,
      });
    }
  };

  const updatePhotoInput = (photos) => {
    let inputButtonText = yesFocused ? "YES" : noFocused ? "NO" : "N/A";
    if (inputButtonText === "YES") {
      setInputGiven(photos.length > 0);
      inputHandler(questionGUID, {
        questionGUID: questionGUID,
        value: inputButtonText,
        textInput: notes,
        photos: photos,
        valid: photos.length > 0,
      });
    } else if (inputButtonText === "NO") {
      setInputGiven(notes.length > 0 && photos.length > 0);
      inputHandler(questionGUID, {
        questionGUID: questionGUID,
        value: inputButtonText,
        textInput: notes,
        photos: photos,
        valid: notes.length > 0 && photos.length > 0,
      });
    } else {
      setInputGiven(notes.length > 0);
      inputHandler(questionGUID, {
        questionGUID: questionGUID,
        value: inputButtonText,
        textInput: notes,
        photos: null,
        valid: notes.length > 0,
      });
    }
  };

  const duplicateImage = (image) => {
    return images.find(
      (img) =>
        image.lastModified === img.lastModified &&
        image.size === img.size &&
        image.type === img.type
    );
  };

  // Handles removing photos from the list.
  const removePhotoHandler = (previewPhoto) => {
    if (!images || images?.length === 0) return;
    let index = images.findIndex(
      (img) =>
        previewPhoto.lastModified === img.lastModified &&
        previewPhoto.size === img.size &&
        previewPhoto.type === img.type
    );
    if (index >= 0) {
      let tempArray = [...images];
      tempArray.splice(index, 1);
      setImages(tempArray);
      updatePhotoInput(tempArray);
    }
    setPreview(null);
  };

  const processUploadedFile = async (e) => {
    setProcessingImages(true);
    setTimeout(() => {
      processImageHandler(e);
    }, 1000);
  };

  // Handles processing of images.
  const processImageHandler = async (e) => {
    let imgFile = await processUploadedImage(e?.target?.files[0]);
    if (!imgFile) return;
    let imgObject = createImgObject(imgFile);

    var reader = new FileReader();
    reader.onload = function (e) {
      var img = document.createElement("img");
      img.onload = () => {
        let dataurl = resizeFile(img);
        let updatedImgObject = { ...imgObject };
        updatedImgObject.url = dataurl;
        updatedImgObject.size = dataurl.length;
        updatedImgObject.questionGUID = questionGUID;
        if (duplicateImage(updatedImgObject)) {
          setStatus("Duplicate image detected!");
          setProcessingImages(false);
          return;
        }
        if (images.length + 1 > maxPhotoCount) {
          setStatus(
            `May not upload more than ${maxPhotoCount} photos per question.`
          );
          setProcessingImages(false);
          return;
        }
        setImages([...images, updatedImgObject]);
        setProcessingImages(false);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(imgFile);
  };

  let yesInputClass = yesFocused
    ? styles.auditInputLiFocused
    : styles.auditInputLi;

  let noInputClass = noFocused
    ? styles.auditInputLiFocused
    : styles.auditInputLi;

  let naInputClass = naFocused
    ? styles.auditInputLiFocused
    : styles.auditInputLi;

  return (
    <AuditQuestionCard questionGUID={questionGUID} inputGiven={inputGiven}>
      <p className={styles.auditQuestion}>{question}</p>
      <ul className={styles.auditInputUL}>
        <li className={yesInputClass} onClick={yesInputHandler}>
          Yes
        </li>
        <li className={noInputClass} onClick={noInputHandler}>
          No
        </li>
        <li className={naInputClass} onClick={naInputHandler}>
          N/A
        </li>
      </ul>
      {(noFocused || naFocused) && (
        <TextArea
          placeholder={"Explain"}
          identifier={questionGUID + "text"}
          onChange={notesChangeHandler}
          value={notes}
        />
      )}
      {(yesFocused || noFocused) && (
        <>
          <div className={styles.imageInputDiv}>
            <CameraInput imageHandler={processUploadedFile} />
          </div>
          <ImagePreviewPrompt
            preview={preview}
            confirmHandler={() => setPreview(null)}
            setConfirmDeletePrompt={() => removePhotoHandler(preview)}
          />
          <ImageListCard
            photos={images}
            setPreview={setPreview}
            deleteAllowed={true}
            removePhotoHandler={removePhotoHandler}
          />
          <ActivityIndicator
            text="Processing Image..."
            loading={processingImages}
          />
        </>
      )}

      <InfoPrompt text={status} statusCleared={() => setStatus("")} />
    </AuditQuestionCard>
  );
};

export default Question;
